import {
  BarChart3,
  Boxes,
  Clock,
  FileText,
  FolderKanban,
  Globe,
  LayoutGrid,
  Milestone,
  Network,
  Paperclip,
  RefreshCcw,
  Scale,
} from 'lucide-react';

import type { Role } from '@/stores/session';
import type { Guide } from '../types';

/** Management / financial project views — admins (+ Accountant for money). */
const PROJECT_ADMIN: Role[] = ['super_admin', 'founder'];
const PROJECT_MONEY: Role[] = ['super_admin', 'founder', 'accountant'];

export const PROJECT_GUIDES: Guide[] = [
  {
    slug: 'projects-overview',
    title: 'How delivery works',
    module: 'projects',
    summary: 'From a won deal to delivered work — projects, work items, time and reporting.',
    icon: FolderKanban,
    body: `**Projects** is where delivery happens — the work you do *after* a deal is
won. A signed contract provisions a project automatically, so sales hands off to
delivery with nothing re-keyed. It's a **native, write-owned** module: you plan,
track and deliver entirely here (no external task tool to keep in sync).

\`\`\`
 CONTRACT ──▶  PROJECT ──▶  WORK ITEMS ──▶  MODULES · CYCLES · VIEWS
 (signed)      (delivery)   (the work)         (organise the work)
                                │
                                ▼
                    TIME (timer + worklogs)
                                │
                                ▼
              REPORTS · BUDGET vs ACTUAL · ALLOCATION
\`\`\`

### Inside a project
Open any project and you get a workspace with tabs:
- **Overview** — the project at a glance: properties, progress, activity.
- **Work Items** — the actual tasks (board, list, table, calendar, timeline).
- **Modules** & **Cycles** — group work by feature/workstream and by sprint.
- **Views** — saved layout + filter combinations you reuse.
- **Pages** — in-project docs/specs (a mini wiki for that project).
- **Attachments** — files linked to the project.
- **Settings** — states, types, labels, members, milestones.

### Across projects
- **Worklogs** — the cross-project timesheet (manual entries + the timer).
- **Resource Allocation, Budget vs Actual, Reports** — the management lens
  (capacity, cost in hours, delivery health) — admin / finance views.

### Who sees what
Every staff member can see projects, work items, log time and read pages &
attachments. Capacity, budget, reports and the client portal are for leadership
and finance.`,
  },
  {
    slug: 'projects-projects',
    title: 'Projects',
    module: 'projects',
    summary: 'The unit of delivery — a workspace for one client engagement.',
    icon: FolderKanban,
    body: `A **Project** is a piece of client work being delivered. It usually starts
life automatically when a **contract is signed**, and links back to the client
**company**.

### How to use it
- The Projects list is a CRM-style view — switch layouts, filter and search,
  and export.
- Each project has an **identifier** (e.g. \`STARK\`) that prefixes its work
  items (\`STARK-1\`, \`STARK-2\`…), a **lead**, **priority**, dates and a status.
- **Visibility** is per project: **public** projects are visible to all staff;
  **private** projects are limited to their **members** (admins always see all).
- Create a project directly when you need one outside the sales flow — you become
  its lead and first member.

### Why it's here
It's the home of delivery — the bridge between *what was sold* and *what gets
done*, keeping client work, tasks, dates and effort together in one record.`,
  },
  {
    slug: 'projects-work-items',
    title: 'Work Items',
    module: 'projects',
    summary: 'The tasks themselves — types, states, and five ways to view them.',
    icon: Boxes,
    body: `**Work Items** are the building blocks of a project — the tasks, bugs and
stories your team actually delivers.

### Types & states
- **Types**: Task, Story, Bug, Sub-task, Epic (each project seeds these; admins
  can add more in Settings).
- **States** move work across a lifecycle — e.g. *Backlog → To Do → In Progress
  → In Review → Ready For QA → In QA → Done* — grouped as backlog / unstarted /
  started / completed / cancelled. **In Review** is for code review (a PR/MR is
  open). Admins tailor and reorder states per project.

### Five layouts (top-right switcher)
- **Board** — Kanban columns by state; drag cards to move them.
- **List** & **Table** — dense rows for scanning and bulk reading.
- **Calendar** — by due date.
- **Timeline (Gantt)** — start/end across time.
Use **Filters** to narrow by state, assignee, priority, label, module or cycle,
and **search** by title. Save a combination as a **View** to reuse it.

### Working an item
Click any item to open its detail drawer — set state, priority, assignees,
labels, dates, estimate, parent, module and cycle, write a rich description, and
start a **timer**. **Assignees can only be project members** (manage them in
Settings ▸ Members).

### Why it's here
This is the day-to-day surface of delivery — everything the team is doing, in the
shape that fits the moment.`,
  },
  {
    slug: 'projects-modules-cycles',
    title: 'Modules & Cycles',
    module: 'projects',
    summary: 'Group work by workstream (modules) and by time-box (cycles).',
    icon: RefreshCcw,
    body: `**Modules** and **Cycles** are two ways to organise a project's work items.

### Modules
A **Module** is a workstream or feature area (e.g. "Billing", "Onboarding").
Assign work items to a module to track a slice of the project end-to-end, with
its own progress.

### Cycles
A **Cycle** is a time-box — a sprint or iteration with a start and end date. Put
work into the active cycle to plan and review what a period should deliver.

### How to use them
- Create modules/cycles from their tabs, then assign items (from the item or the
  board).
- Both tabs carry the same filters as Work Items, and hide the filter bar until
  there's something to filter.

### Why it's here
Big projects need structure. Modules answer *"which part of the product?"*;
cycles answer *"what are we doing this sprint?"*.`,
  },
  {
    slug: 'projects-views',
    title: 'Views',
    module: 'projects',
    summary: 'Save a layout + filters once, reopen it in a click.',
    icon: LayoutGrid,
    body: `A **View** is a saved combination of **layout + filters** for a project's
work items — so the slices you look at often are one click away.

### How to use it
1. On **Work Items**, set a layout (board/list/table/calendar/timeline) and apply
   filters (e.g. *my items, in progress, high priority*).
2. Open **Views ▸ Save current view** and name it.
3. Reopen it any time from the **Views** menu.

### Why it's here
Everyone slices the same work differently — "my open bugs", "this cycle",
"unassigned". Views make those personal lenses durable instead of rebuilt each time.`,
  },
  {
    slug: 'projects-pages',
    title: 'Pages',
    module: 'projects',
    summary: 'In-project docs and specs with a rich block editor.',
    icon: FileText,
    body: `**Pages** is a mini wiki that lives *inside* a project — specs, notes,
runbooks and decisions, kept next to the work they describe.

### How to use it
- Build a tree of pages and sub-pages; drag to re-order or re-parent, or use
  **Move to…** from the ⋯ menu.
- Write with the **block editor** — type \`/\` for commands (headings, lists,
  checklists, tables, callouts, code, images, @mentions).
- A page is a **Draft** until you **Publish** it — drafts are visible only to you;
  publishing shares it with the project. **Lock** a page to freeze it, and use
  **History** to view or restore earlier versions.

### Link & embed other pages
Type \`/\` and pick **Page** (a clickable link to another page), **Page content**
(embed another page's body inline, read-only and always live), or **Child pages**
(a tidy indented list of a page's sub-pages — a built-in table of contents).
Embeds only show pages you could already open, and hovering an embed lets you
switch its mode or remove it. (See the Wiki guide "Linking & embedding pages" for
the full rundown.)

### Archive vs delete
- **Archive** (⋯ menu) takes a page out of the tree and into the **Archived** tab. Its
  sub-pages move up a level so nothing disappears with it, and **Restore** brings it
  back. Archived pages drop out of the Knowledge Base.
- **Delete** — from the **Archived** tab — removes a page for good: gone from the tree,
  from embeds and from the Knowledge Base. Its sub-pages move up a level.
- Not sure? Archive. Delete is for pages that will never come back, like superseded
  drafts.

### Pages vs the Wiki
Use **Pages** for docs tied to one project. Use the standalone **Wiki** for
company-wide knowledge that spans projects (see the Wiki guides).

### Why it's here
Delivery context shouldn't live in scattered docs. Pages keep the "why" beside
the "what".`,
  },
  {
    slug: 'projects-attachments',
    title: 'Attachments',
    module: 'projects',
    summary: 'Files linked to a project — readable by the whole team.',
    icon: Paperclip,
    body: `The **Attachments** tab holds files linked to a project — briefs, assets,
exports, anything the team needs to hand.

### How to use it
- Drag a file onto the dropzone, or click to upload (multiple at once).
- Download or preview (PDFs preview inline); remove a file you uploaded.

### Who can see them
**Any staff member with access to the project** can read and download its
attachments — including employees. (This is separate from the company Documents
library, so project files never expose HR or finance documents.) Deleting a file
is limited to the person who uploaded it or a project admin.

### Why it's here
Project files belong with the project, visible to everyone delivering it — not
locked behind a manager-only document library.`,
  },
  {
    slug: 'projects-milestones',
    title: 'Milestones',
    module: 'projects',
    summary: 'The key dates and deliverables that mark progress.',
    icon: Milestone,
    body: `**Milestones** are the checkpoints on a project — the deliverables and dates
that mark real progress. Work items can be tied to a milestone.

### How to use it
- Add milestones in the project (Settings ▸ Milestones) and see what's due when.
- Use them to keep delivery on track and set expectations with the client.

### Why it's here
Big projects need waypoints. Milestones turn "we're working on it" into a clear,
shared picture of what's done and what's next.`,
  },
  {
    slug: 'projects-worklogs',
    title: 'Time & Worklogs',
    module: 'projects',
    summary: 'Track time two ways — a live timer or manual entries — in one timesheet.',
    icon: Clock,
    body: `**Worklogs** is the single timesheet for the org — every logged hour, across
every project, in one place. Time gets in two ways:

### 1. The work-item timer (recommended)
- Open a work item and hit **Start timer** (or use the header **Start** control).
  A live timer pill appears in the top bar with the elapsed time.
- **Stop & log** asks **what you worked on** — a short note (up to 255
  characters) that becomes the worklog's description, like an Upwork memo. Skip it
  if your org allows; if your org requires notes, you can't log until you write
  one. **Discard** throws the timer away.
- Only one timer runs at a time — starting a new one stops and logs the previous,
  asking for its note first.
- A timer left running is auto-stopped after **9 hours** and flagged for review,
  so a forgotten timer never logs a runaway day.

### 2. Log time manually
- On **Worklogs**, click **Log time**: pick the project, enter hours and a note.
- Hours accept flexible input — \`1h 30min\`, \`90min\` or \`1.5\` all work; they're
  stored as decimal hours and shown back as \`1h 30min\`.
- Edit or delete an entry to correct it — unless a client invoice has already
  billed it: then it's locked until finance reverts that invoice to draft (once
  the invoice is paid, it stays locked).

### Group by
- **Group by** (next to the filters) shows subtotals — entries and time — per
  **user**, **project**, **work item** or **date**, over everything that matches
  your filters (not just the visible page). Click a group to filter the entries to
  it. The choice is kept in the link, so you can share a grouped view.

### Who can log & see what
- You always see and manage **your own** time. **Founder, Super Admin and
  Accountant** can view everyone's worklogs and filter by person.
- Only **Founder / Super Admin** can log time *on behalf of* someone else.
- **Timer notes** (Settings ▸ Organization) are *optional* by default; set them to
  *required* and every worklog needs a note — on stop, when logging or editing by
  hand, and through MCP — for everyone.
- Manual logging can be turned **off org-wide** (Settings ▸ Organization) — then
  time is captured only by the timer, and you can edit only the **note** on your
  entries, not change or delete the time (Founder/Super Admin/Accountant keep
  manual logging regardless).

### Export a timesheet
- **Export PDF** turns the current (filtered) worklog view into a timesheet
  document — a heading with the project, person and period, and a per-employee
  table of entries. Handy to hand a client, or as the backing detail that a client
  invoice attaches automatically (see Finance ▸ *Invoice documents & attachments*).

### Why it's here
Logged time powers **Budget vs Actual**, utilisation and delivery reporting. The
timer makes capturing it effortless; the worklog keeps it honest.`,
  },
  {
    slug: 'projects-resource-allocation',
    title: 'Resource Allocation',
    module: 'projects',
    summary: 'Plan who works on what, and watch team capacity & over-allocation.',
    icon: Network,
    roles: PROJECT_ADMIN,
    body: `**Resource Allocation** is capacity planning — who is assigned to which
project, for how many hours a week, and whether anyone is over-committed.

### How to use it
- Create **allocations** to assign people to projects (hours per week, date range).
- The **utilisation** panel sums each person's active allocations against their
  weekly capacity (default 40h) and flags **over-allocation** with a red badge.
- Pick an **as-of date** to see the plan at any point in time.

### Why it's here
It answers "do we have the people to deliver this?" — turning a pile of projects
into a realistic plan against the team you actually have, before over-load bites.`,
  },
  {
    slug: 'projects-budget-vs-actual',
    title: 'Budget vs Actual',
    module: 'projects',
    summary: 'Compare budgeted hours against the time actually logged.',
    icon: Scale,
    roles: PROJECT_MONEY,
    body: `**Budget vs Actual** is a read-only report comparing each project's
**budgeted hours** against the **actual hours** logged — so you can see, at a
glance, what's on track and what's overrunning.

### How to read it
- **Budget (h)** — the hours planned for the project.
- **Actual (h)** — the hours logged (timer + manual worklogs).
- The gap between them is your early warning on margin.

### Why it's here
Hours are the cost of delivery. Watching budget vs actual keeps projects
profitable — and flags the ones that need a conversation with the client.`,
  },
  {
    slug: 'projects-reports',
    title: 'Reports',
    module: 'projects',
    summary: 'Delivery and effort reporting across projects.',
    icon: BarChart3,
    roles: PROJECT_MONEY,
    body: `**Reports** brings together the delivery picture across all projects —
effort per project, billable share and the link through to **Budget vs Actual**.
Hours read in human form (\`1h 30min\`) with the exact decimal on hover.

### Why it's here
A single management view of how delivery is going org-wide, so leadership can
spot trends and act before small slips become big ones.`,
  },
  {
    slug: 'projects-client-portal',
    title: 'Client Portal',
    module: 'projects',
    summary: 'A read-only window for clients into their own projects.',
    icon: Globe,
    roles: PROJECT_ADMIN,
    body: `The **Client Portal** is the outside-in view: a secure space where a
**client (guest)** sees only **their own** company's projects and invoices —
nothing else in the workspace.

### How it works
- Guests are **deny-by-default** — the portal is scoped strictly to their company.
- A project appears only when you switch on **"Visible in client portal"**
  (Project ▸ Settings ▸ General) — so internal/pre-sales projects stay hidden.
- Clients get a **read-only** view of the project overview, work-item status and
  milestones. Budgets, worklogs, time and internal config are never exposed.

### Why it's here
Clients want visibility without a flood of emails. The portal gives them a
trusted, self-service window into their work while keeping everything else private.`,
  },
];
